import React, { useState } from 'react';
import { Menu, X, LogOut } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { useAppContext } from '../context/AppContext';
import { useToast } from '../context/ToastContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { user, isAdmin, setUser, setIsAdmin } = useAppContext();
  const { addToast } = useToast();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAdmin(false);
      addToast('✅ Logged out successfully', 'success');
      navigate('/');
    } catch (error) {
      addToast(`❌ Logout failed: ${error.message}`, 'error');
    }
  };

  return (
    <nav className="bg-gradient-to-r from-slate-900 to-slate-800 text-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 font-bold text-xl hover:text-blue-400 transition">
            📦 ODI Logistics
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition">Home</Link>
            <Link to="/book-pickup" className="hover:text-blue-400 transition">Book Pickup</Link>
            <Link to="/track" className="hover:text-blue-400 transition">Track Shipment</Link>
            
            {/* ✅ Admin Tab - Only show for admin users */}
            {user && isAdmin && (
              <Link 
                to="/admin" 
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition cursor-pointer font-semibold"
              >
                👨‍💼 Admin
              </Link>
            )}
            {user && user.admin && (
              <Link 
                  to="/admin/manage-users" 
                  className="text-gray-700 hover:text-purple-600 transition-colors duration-300 font-medium"
                >
                  👥 Manage Users
                </Link>
              )}
            
            {!user ? (
              <Link 
                to="/admin-login" 
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition cursor-pointer"
              >
                🔐 Admin Login
              </Link>
            ) : (
              <>
                <span className="text-slate-300 text-sm">{user.email}</span>
                <button
                  onClick={handleLogout}
                  className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg flex items-center gap-2 transition cursor-pointer"
                >
                  <LogOut className="h-4 w-4" /> Logout
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="hover:text-blue-400 transition">
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-4 space-y-2">
            <Link to="/" className="block hover:text-blue-400 transition py-2">Home</Link>
            <Link to="/book-pickup" className="block hover:text-blue-400 transition py-2">Book Pickup</Link>
            <Link to="/track" className="block hover:text-blue-400 transition py-2">Track Shipment</Link>
            
            {/* ✅ Mobile Admin Tab */}
            {user && isAdmin && (
              <Link 
                to="/admin" 
                className="block bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition cursor-pointer text-center font-semibold"
              >
                👨‍💼 Admin
              </Link>
            )}
            
            {!user ? (
              <Link 
                to="/admin-login" 
                className="block bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition cursor-pointer text-center"
              >
                🔐 Admin Login
              </Link>
            ) : (
              <>
                <p className="py-2 text-slate-300 text-sm">{user.email}</p>
                <button
                  onClick={handleLogout}
                  className="w-full bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition cursor-pointer"
                >
                  <LogOut className="h-4 w-4" /> Logout
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;